import { config } from "@/Common/envConfig";
import debounce from "just-debounce-it";
import throttle from "just-throttle";

import type { ChatAuthMessageSocket } from "@/Modules/Chat/Models/WebSocket";
import { MessageTypeWebSocket } from "@/Modules/Chat/Models/WebSocket";
import type { PromiseObject } from "@/Modules/Chat/Models/State";

import { hasValue, isObject } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { danger } from "@/Utils/Debug";

// https://stackoverflow.com/questions/19304157/getting-the-reason-why-websockets-closed-with-close-code-1006
const BROWSER_CLOSE_CODE = 1006;

interface SocketParams {
  userId: string;
  userToken: string;
}

export interface EventFilter<T = undefined> {
  isType: (e: unknown) => boolean;
  handler: (e: unknown, params: T | object) => Promise<undefined | unknown>;
}

interface EventWithNonce {
  nonce: string;
}

export class WebSocketManager {
  private noncesSubscriptions: Record<string, PromiseObject | undefined>;
  private pingInterval: number | undefined;
  private messageQueueNotSend: object[];
  private isAReconnection: boolean;
  private reConnect: () => void;

  constructor(
    private readonly socketParams: SocketParams,
    private readonly initializeBot: () => void,
    private readonly setAnswerLoading: (value: boolean) => void,
    private readonly reconnectionCallback: () => void,
    private readonly messageHandler: (event: unknown) => Promise<unknown | undefined>,
    private webSocket?: WebSocket
  ) {
    this.noncesSubscriptions = {};
    this.messageQueueNotSend = [];
    this.isAReconnection = false;

    this.reConnect = debounce(() => {
      if (!this.isOpen() && !this.isConnecting() && hasValue(this.webSocket)) {
        this.setAnswerLoading(true);
        this.initWebsocketConnection(true);
      }
    }, 2500);

    this.initWebsocketConnection = throttle(this.initWebsocketConnection, 2500, {
      leading: true,
      trailing: false,
    });

    this.initWebsocketConnection();
  }

  initWebsocketConnection(isAReconnection = false) {
    this.isAReconnection = isAReconnection;

    if (this.isOpen()) {
      this.close();
    }

    if (this.isClosed()) {
      this.webSocket = new WebSocket(config.webSocketUrl!);

      this.webSocket.onopen = this.onConnection.bind(this);
      this.webSocket.onmessage = this.messageProcessor.bind(this);
      this.webSocket.onclose = this.onClose.bind(this);
      this.webSocket.onerror = this.onError.bind(this);

      this.sendQueue();
    }
  }

  public authenticate() {
    const authPayload: ChatAuthMessageSocket = {
      caps: 49,
      event_list_size: 0,
      user: this.socketParams.userId,
      signature: this.socketParams.userToken,
      type: MessageTypeWebSocket.AUTH,
      time: Math.floor(Date.now() / 1000),
      nonce: `${Date.now() + Math.floor(Math.random() * 1000)}`,
      fast: true,
    };

    if (hasValue(this.webSocket)) {
      this.webSocket.send(JSON.stringify(authPayload));
    }
  }

  private messageProcessor(message: MessageEvent) {
    if (hasValue(message.data)) {
      try {
        const data = JSON.parse(message.data);

        if (isObject(data)) {
          data.isNew = true;
        }

        this.eventProcessor(data);
      } catch (e: unknown) {
        danger("error processing message", message, e);
      }
    }
  }

  private isNonceSubscribed(event: unknown): event is EventWithNonce {
    if (isObject(event) && event.nonce && isValidString(event.nonce)) {
      if (this.noncesSubscriptions[event.nonce] !== undefined) {
        return true;
      }
    }

    return false;
  }

  private stopPingInterval() {
    if (hasValue(this.pingInterval)) {
      window.clearInterval(this.pingInterval);
    }

    this.pingInterval = undefined;
  }

  private onConnection() {
    if (hasValue(this.webSocket)) {
      this.setAnswerLoading(false);
      this.authenticate();

      if (this.isAReconnection) {
        this.initializeBot();
        this.reconnectionCallback();
      }

      this.pingInterval = window.setInterval(() => {
        this.sendMessage({ type: MessageTypeWebSocket.PING });
      }, config.webSocketPing);

      this.isAReconnection = false;
    }
  }

  private onClose(ev: CloseEvent) {
    if (ev.code === BROWSER_CLOSE_CODE) {
      this.reOpen();
    }
  }

  private onError() {
    this.reOpen();
  }

  public isClosed() {
    return this.webSocket === undefined || this.webSocket.readyState === this.webSocket.CLOSED;
  }

  private reOpen() {
    if (hasValue(this.reConnect)) {
      this.reConnect();
    }
  }

  private sendQueue() {
    if (this.isOpen() && this.messageQueueNotSend.length) {
      const message = this.messageQueueNotSend.pop();

      if (message) {
        this.sendMessage(message);
      }

      if (this.messageQueueNotSend.length) {
        this.sendQueue();
      }
    }
  }

  public async eventProcessor(event: unknown): Promise<undefined | unknown> {
    let returnedValue: unknown | undefined = undefined;

    try {
      returnedValue = await this.messageHandler(event);

      if (this.isNonceSubscribed(event)) {
        this.noncesSubscriptions[event.nonce]?.resolve(returnedValue);
      }

      return returnedValue;
    } catch (e) {
      danger("error processing event", event, e);

      if (this.isNonceSubscribed(event)) {
        this.noncesSubscriptions[event.nonce]?.reject(returnedValue);
      }

      return returnedValue;
    }
  }

  public sendMessage(message: object) {
    try {
      if (hasValue(this.webSocket) && this.isOpen()) {
        this.webSocket.send(JSON.stringify(message));
      } else {
        this.messageQueueNotSend.push(message);

        this.reOpen();
      }
    } catch (e) {
      this.reOpen();
    }
  }

  public subscribeToEventNonce(nonce: string, promise: PromiseObject) {
    this.noncesSubscriptions[nonce] = promise;
  }

  public close() {
    this.stopPingInterval();

    if (hasValue(this.webSocket)) {
      this.webSocket.close();
      this.webSocket = undefined;
    }
  }

  public isOpen(): boolean {
    return hasValue(this.webSocket) && this.webSocket.readyState === this.webSocket.OPEN;
  }

  public isConnecting(): boolean {
    return hasValue(this.webSocket) && this.webSocket.readyState === this.webSocket.CONNECTING;
  }

  public check() {
    if (this.isOpen()) {
      this.sendMessage({ type: MessageTypeWebSocket.PING });
    } else {
      this.reOpen();
    }
  }
}
